import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  RealtimePostgresChangesPayload,
  SupabaseClient,
} from "@supabase/supabase-js";
import {
  clearImpersonationTab,
  getImpersonationSessionId,
  isImpersonationTab,
} from "@pps/auth-client/impersonation";

export interface ImpersonationSession {
  id: string;
  admin_id: string;
  target_app: "crm" | "public";
  reason: string;
  started_at: string;
  expires_at: string;
}

export interface UseImpersonationBannerOptions {
  /**
   * Intervalo de refetch em ms. Default: 30s. Combinado com o timer local de
   * expiração e o Realtime, garante que a aba de suporte se encerra em até
   * 30s depois do fim da sessão.
   */
  pollIntervalMs?: number;
  /**
   * Chamado quando o claim ou o encerramento falham (IR-RX-005/IR-OBS-003).
   * Os wrappers dos apps ligam no Sentry com tags
   * `{ feature: 'impersonation', action }`. Nunca lança para o hook.
   */
  onError?: ImpersonationBannerErrorHandler;
}

export type ImpersonationBannerAction = "claim" | "end";

export type ImpersonationBannerErrorHandler = (
  error: unknown,
  context: { action: ImpersonationBannerAction },
) => void;

/**
 * Volta a aba ao app normal: tira `?impersonation=` da URL (senão a marca
 * seria recapturada no load) e recarrega. Exportado para os testes.
 */
export function reloadWithoutImpersonation(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("impersonation");
    url.hash = "";
    window.location.replace(url.toString());
  } catch {
    /* sem window (SSR) */
  }
}

export interface UseImpersonationBannerResult {
  session: ImpersonationSession | null;
  isLoading: boolean;
  /**
   * true quando a sessão de suporte desta aba terminou (pelo botão, pelo TTL
   * ou por um admin no painel) e `window.close()` não fechou a aba — o banner
   * mostra a tela "sessão de suporte encerrada".
   */
  ended: boolean;
  /**
   * Encerra a sessão chamando a Edge Function `end-impersonation` (que
   * também revoga a auth session desta aba) e finaliza a aba. Retorna `true`
   * se o servidor confirmou, `false` em erro — a aba é finalizada mesmo assim.
   */
  end: () => Promise<boolean>;
}

const POLL_DEFAULT_MS = 30_000;

/**
 * Banner da ABA DE SUPORTE (impersonação de owner pelo admin).
 *
 * Só existe na aba marcada por `?impersonation=<id>` (`isImpersonationTab`,
 * contrato em `@pps/auth-client/impersonation`). Nas sessões reais do owner
 * — celular, outro navegador, outra aba — o hook não faz NADA: sem query,
 * sem polling, sem Realtime, sem signOut. A impersonação é invisível ao
 * cliente (decisão do usuário, 06/10/2026).
 *
 * Na aba de suporte:
 *   1. ao montar, vincula a sessão de impersonação à auth session desta aba
 *      (RPC `claim_impersonation_session`) — é o que libera a leitura da
 *      linha pela RLS e permite revogar SÓ esta auth session no fim;
 *   2. lê a sessão (poll + Realtime) e arma o timer do TTL;
 *   3. claim que falha (link forjado `?impersonation=<uuid>`, sessão já
 *      encerrada, aba sem login) NÃO mostra a tela de encerrada — ela só
 *      existe para quem teve uma sessão de suporte de verdade: signOut local
 *      (só o sessionStorage da aba), limpa a marca e recarrega o app normal,
 *      sem o parâmetro;
 *   4. qualquer saída (botão, TTL, encerramento por admin) faz
 *      `signOut({ scope: "local" })` — nunca global, que derrubaria o owner
 *      em todos os aparelhos —, limpa o sessionStorage da aba e tenta
 *      `window.close()`; se o navegador não deixar, `ended` vira true.
 *
 * Doc: docs/funcionalidades/impersonacao-de-owner.md
 */
export function useImpersonationBanner(
  supabase: SupabaseClient,
  options: UseImpersonationBannerOptions = {},
): UseImpersonationBannerResult {
  const { pollIntervalMs = POLL_DEFAULT_MS, onError } = options;
  // Ref: o handler muda a cada render do wrapper e não deve reiniciar efeitos.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const report = useCallback(
    (error: unknown, action: ImpersonationBannerAction) => {
      try {
        onErrorRef.current?.(error, { action });
      } catch {
        /* o reporte nunca derruba a aba */
      }
    },
    [],
  );
  // Decidido uma vez: a marca da aba não muda durante a vida do componente.
  const [active] = useState<boolean>(() => isImpersonationTab());
  const [sessionId] = useState<string | null>(() =>
    active ? getImpersonationSessionId() : null,
  );
  const [session, setSession] = useState<ImpersonationSession | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(active);
  const [claimed, setClaimed] = useState(false);
  const [ended, setEnded] = useState(false);
  const mountedRef = useRef(true);
  const finishedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Fim da aba de suporte: signOut LOCAL, limpa a aba e tenta fechá-la.
   * `claimed: false` (o claim nunca deu certo) não mostra a tela de
   * encerrada: recarrega o app normal sem a marca (V-006).
   */
  const finish = useCallback(async (opts: { claimed?: boolean } = {}): Promise<void> => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {
      /* a sessão pode já ter sido revogada no servidor — segue limpando */
    }
    clearImpersonationTab();
    if (opts.claimed === false) {
      reloadWithoutImpersonation();
      return;
    }
    if (mountedRef.current) {
      setSession(null);
      setEnded(true);
    }
    try {
      window.close();
    } catch {
      /* aba não aberta por script: fica a tela de "sessão encerrada" */
    }
  }, [supabase]);

  // 1. Claim uma vez, ao montar.
  const claimStartedRef = useRef(false);
  useEffect(() => {
    if (!active || !sessionId || claimStartedRef.current) return;
    claimStartedRef.current = true;
    void (async () => {
      const { error } = await supabase.rpc("claim_impersonation_session", {
        p_session_id: sessionId,
      });
      if (!mountedRef.current) return;
      if (error) {
        // Sessão encerrada/expirada, de outro usuário, link forjado ou aba
        // sem login: não há sessão de suporte aqui — volta ao app normal.
        // eslint-disable-next-line no-console
        console.error("[impersonation-banner] claim failed", error.message);
        report(error, "claim");
        setIsLoading(false);
        void finish({ claimed: false });
        return;
      }
      setClaimed(true);
    })();
  }, [active, sessionId, supabase, finish, report]);

  const fetchSession = useCallback(async () => {
    if (!sessionId) return;
    const { data, error } = await supabase
      .from("impersonation_sessions")
      .select("id, admin_id, target_app, reason, started_at, expires_at, ended_at")
      .eq("id", sessionId)
      .maybeSingle();
    if (!mountedRef.current) return;
    setIsLoading(false);
    if (error) {
      // Falha de leitura transitória: mantém o estado e tenta no próximo poll.
      return;
    }
    const row = data as (ImpersonationSession & { ended_at: string | null }) | null;
    if (!row || row.ended_at || new Date(row.expires_at).getTime() <= Date.now()) {
      void finish();
      return;
    }
    setSession({
      id: row.id,
      admin_id: row.admin_id,
      target_app: row.target_app,
      reason: row.reason,
      started_at: row.started_at,
      expires_at: row.expires_at,
    });
  }, [sessionId, supabase, finish]);

  // 2. Poll — só na aba de suporte, depois do claim.
  useEffect(() => {
    if (!active || !claimed) return;
    void fetchSession();
    const interval = globalThis.setInterval(fetchSession, pollIntervalMs);
    return () => globalThis.clearInterval(interval);
  }, [active, claimed, fetchSession, pollIntervalMs]);

  // Timer do TTL.
  useEffect(() => {
    if (!session) return;
    const msUntilExpiry = new Date(session.expires_at).getTime() - Date.now();
    if (msUntilExpiry <= 0) {
      void finish();
      return;
    }
    const timer = globalThis.setTimeout(() => {
      void finish();
    }, msUntilExpiry + 1000);
    return () => globalThis.clearTimeout(timer);
  }, [session, finish]);

  // Realtime: um admin encerrou a sessão pelo painel — não espera o poll.
  // A RLS (impersonation_target_read_claimed) só entrega o evento para a
  // auth session vinculada, isto é, esta aba.
  useEffect(() => {
    if (!active || !claimed || !sessionId) return;
    const channel = supabase
      .channel(`impersonation-banner-${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "impersonation_sessions",
          filter: `id=eq.${sessionId}`,
        },
        (
          payload: RealtimePostgresChangesPayload<{
            id: string;
            ended_at: string | null;
          }>,
        ) => {
          const updated = payload.new as { ended_at?: string | null };
          if (updated?.ended_at) void finish();
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [active, claimed, sessionId, supabase, finish]);

  const end = useCallback(async (): Promise<boolean> => {
    if (!sessionId) return false;
    let ok = true;
    try {
      const { error } = await supabase.functions.invoke("end-impersonation", {
        body: { session_id: sessionId },
      });
      if (error) {
        // eslint-disable-next-line no-console
        console.error("[impersonation-banner] end failed", error);
        report(error, "end");
        ok = false;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[impersonation-banner] end threw", err);
      report(err, "end");
      ok = false;
    } finally {
      // Seja qual for o resultado, a aba de suporte termina aqui. Se o
      // servidor falhou, o cron impersonation-expire encerra e revoga no TTL.
      await finish();
    }
    return ok;
  }, [sessionId, supabase, finish, report]);

  return useMemo(
    () => ({ session, isLoading, ended, end }),
    [session, isLoading, ended, end],
  );
}
